/**
 * apps/web/src/pages/LoginPage.tsx
 * -------------------------------------------------------
 * Purpose:
 *   Simple email/password sign-in screen.
 *
 * High-level data flow:
 *   - The form POSTs to the `/login` route action (router.tsx -> loginAction).
 *   - loginAction calls Better Auth (`/api/auth/sign-in/email`) which sets an HttpOnly session cookie.
 *   - On success, the user is redirected to `/` where rootLoader verifies the session via `/api/me`.
 */

import React from 'react';
import { Box, Button, Card, Center, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { Form, useActionData, useNavigation } from 'react-router-dom';

/** Action data shape returned by loginAction() on failures. */
type LoginActionData = { error?: string };

export function LoginPage(): React.ReactElement {
  /** Action data returned by the route action (used to display inline errors). */
  const actionData = useActionData() as LoginActionData | undefined;

  /** Navigation state to disable the form during submission. */
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  return (
    <Center h="100vh">
      <Box w={420} px="md">
        <Stack gap="md">
          <Title order={2}>TinyPro Portal</Title>
          <Text c="dimmed">Sign in to continue.</Text>

          <Card withBorder radius="md" p="lg">
            <Form method="post">
              <Stack gap="sm">
                <TextInput
                  name="email"
                  label="Email"
                  placeholder="admin@local.dev"
                  required
                  autoComplete="email"
                />
                <PasswordInput
                  name="password"
                  label="Password"
                  placeholder="Your password"
                  required
                  autoComplete="current-password"
                />

                {actionData?.error ? (
                  <Text c="red" size="sm">
                    {actionData.error}
                  </Text>
                ) : null}

                <Button type="submit" loading={isSubmitting} fullWidth>
                  Sign in
                </Button>
              </Stack>
            </Form>
          </Card>

          <Text size="xs" c="dimmed">
            Dev tip: run the bootstrap script to create a default admin user.
          </Text>
        </Stack>
      </Box>
    </Center>
  );
}
